/**
 * Core typings and event names for the Shopify Mini-Cart.
 *
 * - Provides minimal cart typings used across the mini-cart internals and UI.
 * - Exposes `CartEvents` constant for all emitted/consumed CustomEvent names.
 * - Used by services, the fetch interceptor, and UI elements to coordinate behavior.
 */

/**
 * A single reward tier in progress bar.
 * Extend as needed by consumers.
 */
export interface CartRewardTier {
  currencySymbol: string;
  thresholdAmount: number;
  thresholdDisplayAmount: string;
  encouragementMessage: string;
  element: HTMLElement;
}

/**
 * A single line item within the Shopify cart.
 * Extend as needed by consumers.
 */
export type CartLine = {
  id: number;
  properties: Record<string, string>;
  quantity: number;
  variant_id: number;
  key: string;
  title: string;
  product_title: string;
  variant_title: any;
  price: number;
  original_price: number;
  discounted_price: number;
  line_price: number;
  original_line_price: number;
  presentment_price: number;
  total_discount: number;
  discounts: Array<any>;
  sku: string;
  grams: number;
  vendor: string;
  taxable: boolean;
  product_id: number;
  product_has_only_default_variant: boolean;
  gift_card: boolean;
  final_price: number;
  final_line_price: number;
  url: string;
  featured_image: {
    alt: string;
    aspect_ratio: number;
    height: number;
    url: string;
    width: number;
  };
  image: string;
  handle: string;
  requires_shipping: boolean;
  product_type: string;
  untranslated_product_title: string;
  product_description: string;
  untranslated_variant_title: any;
  variant_options: Array<string>;
  options_with_values: Array<{
    name: string;
    value: string;
  }>;
  line_level_discount_allocations: Array<any>;
  line_level_total_discount: number;
  selling_plan_allocation: {
    price_adjustments: Array<any>;
    price: number;
    compare_at_price: number;
    per_delivery_price: number;
    selling_plan: {
      id: number;
      name: string;
      description: any;
      options: Array<{
        name: string;
        position: number;
        value: string;
      }>;
      recurring_deliveries: boolean;
      price_adjustments: Array<any>;
      checkout_charge: {
        value_type: string;
        value: number;
      };
    };
  };
};

/**
 * Minimal Shopify cart shape shared throughout the mini-cart codebase.
 */
export interface Cart {
  token?: string;
  item_count: number;
  total_price: number;
  items: CartLine[];
  note?: string | null;
  attributes?: Record<string, string>;
  [key: string]: any;
  original_total_price: number;
  total_discount: number;
  total_weight: number;
  requires_shipping: boolean;
  currency: string;
  items_subtotal_price: number;
  discount_codes: Array<unknown>;
}

export type CartSections = string[];

/**
 * All CustomEvent names used by the mini-cart.
 *
 * These are emitted by the `CartService`, `CartFetchInterceptor`, and UI components,
 * and listened to via the `CartEventBus`.
 */
export const CartEvents = {
  Request: 'cart:request',
  Response: 'cart:response',
  Error: 'cart:error',

  Changed: 'cart:changed',
  DebouncedChange: 'cart:changed:debounced',

  FetchRequest: 'cart:fetch:request',
  FetchResponse: 'cart:fetch:response',
  FetchError: 'cart:fetch:error',

  // Core initialised marker
  Ready: 'mini-cart:ready',

  // Command-style events other code can dispatch to invoke actions
  CmdGet: 'mini-cart:get',
  CmdAdd: 'mini-cart:add',
  CmdChange: 'mini-cart:change',
  CmdUpdate: 'mini-cart:update',
  CmdClear: 'mini-cart:clear',

  // Lifecycle markers around specific actions
  AddStart: 'cart:add:start',
  AddSuccess: 'cart:add:success',
  AddError: 'cart:add:error',

  ChangeStart: 'cart:change:start',
  ChangeSuccess: 'cart:change:success',
  ChangeError: 'cart:change:error',

  UpdateStart: 'cart:update:start',
  UpdateSuccess: 'cart:update:success',
  UpdateError: 'cart:update:error',

  ClearStart: 'cart:clear:start',
  ClearSuccess: 'cart:clear:success',
  ClearError: 'cart:clear:error',

  UIUpdate: 'mini-cart:ui:update',
  UIOpen: 'mini-cart:ui:open',
  UIClose: 'mini-cart:ui:close',

  UIOpened: 'mini-cart:ui:opened',
  UIClosed: 'mini-cart:ui:closed',
} as const;

export async function graphql(query: string, variables?: Record<string, any>) {
  const response = await fetch(`${window.Shopify.routes.root}api/2026-01/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  });

  const data = await response.json();
  return data;
}

/**
 * Enforce a raw ID into a Shopify GraphQL ID for the specified type
 *
 * @param id The raw ID
 * @param type The type of the resource
 * @returns The GraphQL ID
 */
export function enforceGraphQLID(id: string, type: string) {
  // Strip everything non-numeric from id
  const match = id.match(/\d+/);
  if (!match) {
    throw new Error(`Invalid raw ID: ${id}`);
  }

  id = match[0];

  return `gid://shopify/${type}/${id}`;
}

/**
 * Strongly-typed CustomEvent detail payloads for each CartEvent.
 */
export interface CartEventDetailMap {
  [CartEvents.Request]: { endpoint: string; method: string; payload?: any };
  [CartEvents.Response]: { endpoint: string; method: string; cart: Cart };
  [CartEvents.Error]: { endpoint: string; method: string; error: unknown };
  [CartEvents.Changed]: { cart: Cart; reason?: 'add' | 'change' | 'update' | 'clear' | 'get' };
  [CartEvents.DebouncedChange]: {
    cart: Cart;
    reason?: 'add' | 'change' | 'update' | 'clear' | 'get';
  };

  [CartEvents.FetchRequest]: { url: string; method: string; init?: RequestInit };
  [CartEvents.FetchResponse]: { url: string; method: string; status: number; data: any };
  [CartEvents.FetchError]: { url: string; method: string; error: unknown };

  [CartEvents.Ready]: undefined;

  [CartEvents.CmdGet]: undefined;
  [CartEvents.CmdAdd]: AddToCartPayload;
  [CartEvents.CmdChange]: ChangeLinePayload;
  [CartEvents.CmdUpdate]: UpdateCartPayload;
  [CartEvents.CmdClear]: undefined;

  [CartEvents.AddStart]: AddToCartPayload;
  [CartEvents.AddSuccess]: { payload: AddToCartPayload; cart: Cart };
  [CartEvents.AddError]: { payload: AddToCartPayload; error: unknown };

  [CartEvents.ChangeStart]: ChangeLinePayload;
  [CartEvents.ChangeSuccess]: { payload: ChangeLinePayload; cart: Cart };
  [CartEvents.ChangeError]: { payload: ChangeLinePayload; error: unknown };

  [CartEvents.UpdateStart]: UpdateCartPayload;
  [CartEvents.UpdateSuccess]: { payload: UpdateCartPayload; cart: Cart };
  [CartEvents.UpdateError]: { payload: UpdateCartPayload; error: unknown };

  // ClearSuccess can be emitted without a cart by interceptors; mark cart optional
  [CartEvents.ClearStart]: undefined;
  [CartEvents.ClearSuccess]: { cart?: Cart } | undefined;
  [CartEvents.ClearError]: { error: unknown };

  [CartEvents.UIUpdate]: { action: 'open' | 'close' | string };
  [CartEvents.UIOpen]: undefined;
  [CartEvents.UIClose]: undefined;

  [CartEvents.UIOpened]: undefined;
  [CartEvents.UIClosed]: undefined;
}

export type CartEventName = (typeof CartEvents)[keyof typeof CartEvents];
export type TypedCartEvent<N extends CartEventName> = CustomEvent<CartEventDetailMap[N]>;

/**
 * Payload for adding one or more items to the cart.
 */
export interface AddToCartItem {
  id?: number; // variant id
  quantity?: number;
  properties?: Record<string, any> | null;
  // Alternative keys some integrations use
  variantId?: number;
}

/**
 * Request body for `/cart/add.js`.
 */
export interface AddToCartPayload {
  items: AddToCartItem[];
  sections?: CartSections;
}

/**
 * Request body for `/cart/change.js`.
 * Provide either `id` (line item key) or `line` (1-indexed position).
 */
export interface ChangeLinePayload {
  id?: string; // line item key
  line?: number; // 1-indexed line position
  quantity: number;
  properties?: Record<string, any> | null;
  sections?: CartSections;
}

/**
 * Request body for `/cart/update.js`.
 */
export type UpdateCartPayload =
  | {
      note?: string | null;
      attributes?: Record<string, string | null>;
      sections?: CartSections;
      discount?: string;
    }
  // Quantity updates for specific line items
  | {
      updates: {
        [key: number]: number;
      };
    };
